import { folder, useControls } from 'leva';
import type { StoreType } from 'leva/dist/declarations/src/types';
import type {
  InputWithSettings,
  MergedInputWithSettings,
  Vector2dInput,
  Vector2dSettings,
  VectorObj,
} from 'leva/plugin';
import { useMemo } from 'react';
import type { DeepWritable } from 'ts-essentials';
import { mediaPicker, multiSelect } from './plugins';

const Controls = {
  input: {
    input: {
      value: '',
    } as InputWithSettings<string>,
  },
  media: {
    file: mediaPicker({
      input: {
        value: {
          id: null,
          mediaItemUrl: null,
        },
        type: 'image',
      },
    }),
  },
  styles: {
    backgroundColor: {
      value: '#000',
      label: 'Background Color',
    } as InputWithSettings<string>,
    color: {
      value: '#ffffff',
      label: 'Text Color',
    } as InputWithSettings<string>,
    borderRadius: {
      min: 0,
      max: 100,
      value: 0,
      step: 0.5,
      label: 'border radius',
    } as InputWithSettings<number>,
    fontSize: {
      min: 1,
      max: 10,
      value: 3,
      step: 0.5,
      label: 'font size',
    } as InputWithSettings<number>,
    width: {
      options: ['auto', 'fit-content', 'min-content', 'max-content'],
      value: 'auto',
      label: 'width',
    } as InputWithSettings<string>,
    padding: {
      value: {
        x: 0,
        y: 0,
      },
      step: 1,
      min: 0,
      max: 100,
      label: 'padding',
    } as MergedInputWithSettings<VectorObj, Vector2dSettings>,
    margin: {
      value: {
        x: 0,
        y: 0,
      },
      step: 1,
      min: 0,
      max: 100,
      label: 'margin',
    } as MergedInputWithSettings<VectorObj, Vector2dSettings>,
  },
  conditions: {
    pages: multiSelect({
      input: {
        value: ['all'],
        options: ['home', 'category', 'znglist', 'hub', 'single'],
        config: {
          type: 'chips',
          selectAll: 'all',
          selectNone: 'none',
        },
      },
      label: 'Show on Page',
    }),
  },
} as const;

type ControlsType = DeepWritable<typeof Controls>;

type ControlKeyValue<T extends keyof ControlsType> = {
  [K in keyof ControlsType[T]]: ControlsType[T][K] extends Vector2dInput
    ? { x: number; y: number }
    : ControlsType[T][K] extends { value: infer V }
      ? V
      : ControlsType[T][K] extends { __customInput: infer I }
        ? I
        : never;
};

export type { ControlKeyValue as UiMetaFieldKeyValue };

type UseUiMetaFieldProps = {
  config: {
    name: string;
    store?: StoreType;
  };
  input?: {
    enabled?: boolean;
    data?: ControlKeyValue<'input'>;
  };
  media?: {
    enabled?: boolean;
    data?: ControlKeyValue<'media'>;
  };
  styles?: {
    enabled?: boolean;
    data?: ControlKeyValue<'styles'>;
  };
  conditions?: {
    enabled?: boolean;
    data?: ControlKeyValue<'conditions'>;
  };
};

export const useUiMetaField = (props: UseUiMetaFieldProps) => {
  const showInputPanel = useMemo(() => props?.input?.enabled !== false, [props?.input?.enabled]);
  const showMediaPanel = useMemo(() => props?.media?.enabled !== false, [props?.media?.enabled]);
  const showStylesPanel = useMemo(() => props?.styles?.enabled !== false, [props?.styles?.enabled]);
  const showConditionsPanel = useMemo(() => props?.conditions?.enabled !== false, [props?.conditions?.enabled]);

  const input = useControls(
    props.config.name,
    {
      input: folder(mergeControlFromKeyValue<'input'>('input', props?.input?.data), { render: () => showInputPanel }),
    },
    {
      ...(props?.config?.store ? { store: props.config.store } : {}),
    },
    [showInputPanel]
  );

  const media = useControls(
    props.config.name,
    {
      media: folder(mergeControlFromKeyValue<'media'>('media', props?.media?.data), { render: () => showMediaPanel }),
    },
    {
      ...(props?.config?.store ? { store: props.config.store } : {}),
    },
    [showMediaPanel]
  );

  const styles = useControls(
    props.config.name,
    {
      styles: folder(mergeControlFromKeyValue<'styles'>('styles', props?.styles?.data), {
        render: () => showStylesPanel,
      }),
    },
    {
      ...(props?.config?.store ? { store: props.config.store } : {}),
    },
    [showStylesPanel]
  );

  const conditions = useControls(
    props.config.name,
    {
      conditions: folder(mergeControlFromKeyValue<'conditions'>('conditions', props?.conditions?.data), {
        render: () => showConditionsPanel,
      }),
    },
    {
      ...(props?.config?.store ? { store: props.config.store } : {}),
    },
    [showConditionsPanel]
  );

  const formattedStyles: React.CSSProperties = useMemo(() => {
    let s: typeof styles | undefined = styles;
    if (!styles?.backgroundColor) {
      s = props?.styles?.data;
    }

    return formatStyles(s as ControlKeyValue<'styles'>);
  }, [styles, props?.styles]);

  return {
    input: !!input?.input ? input : props?.input?.data,
    media: !!media?.file ? media : props?.media?.data,
    styles: !!styles ? styles : props?.styles,
    formattedStyles,
    conditions: !!conditions ? conditions : props?.conditions?.data,
  };
};

export const formatStyles = (styles: ControlKeyValue<'styles'> | undefined) => {
  if (!styles) {
    return {} as React.CSSProperties;
  }

  return {
    ...styles,
    margin: `${styles?.margin?.y}px ${styles?.margin?.x}px`,
    padding: `${styles?.padding?.y}px ${styles?.padding?.x}px`,
    borderRadius: `${styles?.borderRadius}px`,
    fontSize: `${styles?.fontSize}rem`,
  } as React.CSSProperties;
};

function mergeControlFromKeyValue<T extends keyof ControlsType>(key: T, value?: ControlKeyValue<T>): ControlsType[T] {
  const control = { ...Controls[key] } as ControlsType[T];

  Object.keys(control).forEach((k) => {
    const keyValue = value?.[k as keyof ControlsType[T]];
    const controlProp = control[k as keyof ControlsType[T]];

    if (keyValue) {
      control[k as keyof ControlsType[T]] = {
        ...controlProp,
        value: keyValue ?? (controlProp as { value: unknown }).value,
      } as ControlsType[T][keyof ControlsType[T]];
    }

    type CustomControlType = {
      __customInput: {
        input: {
          value: typeof keyValue;
        };
      };
    };

    if (
      keyValue &&
      Object.prototype.hasOwnProperty.call(controlProp, '__customInput') &&
      Object.prototype.hasOwnProperty.call((controlProp as CustomControlType).__customInput, 'input')
    ) {
      control[k as keyof ControlsType[T]] = {
        ...controlProp,
        __customInput: {
          input: {
            ...(controlProp as CustomControlType).__customInput.input,
            value: keyValue ?? (controlProp as CustomControlType).__customInput.input.value,
          },
        },
      };
    }
  });

  return control;
}
